import clsx from 'clsx';
import { HTMLAttributes, RefObject, useEffect, useRef } from 'react';
import {
  containModalTabFocus,
  focusModalContent,
  isolateModalForAssistiveTechnology,
} from '../../../utils/assistiveTechnologyModalIsolation';

interface ModalContainerProps extends HTMLAttributes<HTMLDivElement> {
  visible: boolean;
  enforceAction?: boolean;
  modalMainDivStyle?: string;
  modalChildDivStyle?: string;
  modalTitle?: string;
  modalDescription?: string;
  initialFocusRef?: RefObject<HTMLElement>;
}

const ModalContainer = ({
  visible,
  enforceAction,
  children,
  modalMainDivStyle,
  modalChildDivStyle,
  modalTitle,
  modalDescription,
  initialFocusRef,
}: ModalContainerProps): JSX.Element => {
  const modalRef = useRef<HTMLDivElement>(null); // Ref for the modal

  useEffect(() => {
    if (!visible) return;

    const timerId = window.setTimeout(() => {
      focusModalContent(modalRef.current, initialFocusRef?.current);
    }, 0);

    return () => window.clearTimeout(timerId);
  }, [visible, initialFocusRef]);

  useEffect(() => {
    if (!visible) return;

    return isolateModalForAssistiveTechnology(modalRef.current);
  }, [visible]);

  useEffect(() => {
    if (!visible || !modalRef.current) return;

    const currentModal = modalRef.current;
    let redirectingFocus = false;

    const handleKeyDown = (event: KeyboardEvent) => {
      containModalTabFocus(event, currentModal);
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (redirectingFocus || !currentModal.isConnected) return;

      const target = event.target as Node | null;

      if (!target || currentModal.contains(target)) return;

      redirectingFocus = true;
      focusModalContent(currentModal, initialFocusRef?.current);
      window.setTimeout(() => {
        redirectingFocus = false;
      }, 0);
    };

    currentModal.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      currentModal.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [visible, initialFocusRef]);

  useEffect(() => {
    if (visible && enforceAction) {
      document.body.style.overflow = 'hidden'; // Prevent scrolling on the body
    } else {
      document.body.style.overflow = ''; // Re-enable scrolling on the body when modal is not visible
    }
    return () => {
      document.body.style.overflow = ''; // Ensure scrolling is enabled when component unmounts
    };
  }, [visible, enforceAction]);

  return (
    <div
      data-testid="modalBackground"
      className={clsx('fixed top-0 left-0 bg-natural-700 bg-opacity-30 z-50', {
        hidden: !visible,
        'w-full h-full': enforceAction,
      })}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        data-testid="modalMainDiv"
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitle ? modalTitle : undefined}
        className={clsx(
          'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 overflow-x-hidden overflow-y-auto w-[90%] sm:w-auto',
          {
            hidden: !visible,
          },
          modalMainDivStyle,
        )}
      >
        <div
          data-testid="modalChildDiv"
          className={clsx(
            'border-2 border-natural-300 bg-natural-100 rounded-md p-md',
            modalChildDivStyle,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default ModalContainer;
